// Apopilo: Tutor / Apopilo offers (APOPILO posts), requests, stays, reviews and the caller's balance (room-stc /apopilo/*).
import client from "./client";
import {
  call, getUserPostsByType, getWorldPosts, type ApopiloKind, type ContentId, type EventMapBounds, type Page, type PostV2,
  normalizePage, normalizePost, type WallAuthor,
} from "./content";

export type { ApopiloInfo, ApopiloInput, ApopiloKind, ApopiloViewerStatus } from "./content";

export interface ApopiloRequest { id: string; postId: string; status: string; paymentId: string | null }
export interface ApopiloStay {
  id: string; postId: string; requestId: string; providerUserId: string; guestUserId: string; kind: ApopiloKind;
  startsOn: string; endsOn: string; units: number; status: string; cancelledByUserId: string | null;
}
/** One row of the provider's requests list; rating = the requester's average score as a reviewee (null: none yet). */
export interface ApopiloRequestRow { id: string; status: string; createdAt: string; requester: WallAuthor; rating: number | null }
export interface ApopiloRequests { received: ApopiloRequestRow[]; accepted: ApopiloRequestRow[] }
export interface ApopiloReviewView {
  id: string; stayId: string; reviewerUserId: string; revieweeUserId: string; score: number; review: string; createdAt: string;
}
/** GET apopilo/me; `available` = balance minus nights reserved by the caller's running stays (compare it with a night offer). */
export interface ApopiloMe {
  balance: number; available: number;
  tutoringDaysGiven: number; tutoringDaysReceived: number; nightsHosted: number; nightsSpent: number;
  stats: { completed: number; inProgress: number; unitsOffered: number; unitsDone: number };
  ieupTeamUserId: string | null;
}

const BASE = "/api/content/v2";

/** 201 (new request) and 200 (the caller's open request, a retried take) both resolve to the request. */
export const takeOffer = (postId: ContentId) => call<{ request: ApopiloRequest }>(client.post(`${BASE}/posts/${postId}/apopilo/requests`));
export const listRequests = (postId: ContentId) => call<ApopiloRequests>(client.get(`${BASE}/posts/${postId}/apopilo/requests`));
export const repostOffer = (postId: ContentId) => call<{ postId: string; status: string }>(client.post(`${BASE}/posts/${postId}/apopilo/repost`));

export const acceptRequest = (id: string) => call<{ stay: ApopiloStay }>(client.post(`${BASE}/apopilo/requests/${id}/accept`));
export const rejectRequest = (id: string) => call<{ request: ApopiloRequest }>(client.post(`${BASE}/apopilo/requests/${id}/reject`));
export const withdrawRequest = (id: string) => call<{ request: ApopiloRequest }>(client.post(`${BASE}/apopilo/requests/${id}/withdraw`));
export const cancelStay = (id: string) => call<{ stay: ApopiloStay }>(client.post(`${BASE}/apopilo/stays/${id}/cancel`));
export const reviewStay = (id: string, score: number, review: string) =>
  call<{ review: ApopiloReviewView }>(client.post(`${BASE}/apopilo/stays/${id}/review`, { score, review }));

export const getApopiloMe = () => call<ApopiloMe>(client.get(`${BASE}/apopilo/me`));

/** The Apopilo wall: open offers that have not started (optionally one kind). */
export const getApopiloWall = (kind?: ApopiloKind, cursor?: string | null, limit = 20) => getWorldPosts("APOPILO", { kind }, cursor, limit);

export const getUserApopilo = (userId: string, cursor?: string | null, limit = 20) => getUserPostsByType(userId, "APOPILO", cursor, limit);

/** Takeable offers with a place in the viewport. */
export const getApopiloMap = (b: EventMapBounds) =>
  call(
    client.get(`${BASE}/apopilo/map`, { params: { lat1: b.lat1, lng1: b.lng1, lat2: b.lat2, lng2: b.lng2 } }),
    normalizePage(normalizePost) as (v: unknown) => Page<PostV2>,
  );
